/* eslint-disable max-len */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { CustomersFacade } from '@domain/application/facade';
import { SessionService } from '@domain/application/observable-services/session.service';
import { onOutsideTaxUserInput } from '@modules/business-accounts/forms/outside-tax/outside-tax.form';
import {
  appendPartnerCEDataForm,
  appendPartnerListForm,
  createPartnerCEDataForm,
  createPartnerForm,
  createPartnersListForm,
  getPartnerListFormInstance,
  onPartnerFormUserInput,
  removePartnerCEDataForm,
  removePartnerListForm,
} from '@modules/business-accounts/forms/partners-list-form/partners-list.form';
import {
  getSectorDataClientFormInstance
} from '@modules/business-accounts/forms/utils/data-step/natural-person/builders/natural-person-data-client-builder.class';
import {
  getSectorDataNonClientFormInstance
} from '@modules/business-accounts/forms/utils/data-step/natural-person/builders/natural-person-data-non-client-builder.class';
import { ParametersService } from '@modules/business-accounts/services/parameters/parameters.service';
import { setMaxDatePicker } from '@utils/functions/datepicker';
import { Unsubscriber } from '@utils/mixins/unsubscribe.mixin';
import { catchError, startWith, tap } from 'rxjs';
import { IStep } from '../../utils/interfaces/step.interface';
import { Rubro } from '../natural-person-data-step/natural-person-data-step.component';
import { TraceService } from '@modules/business-accounts/services/trace/trace.service';
import { VisitEvent } from '@modules/business-accounts/utils/classes/visit-event.class';
import { TraceEventName } from '@modules/business-accounts/utils/enums/trace-events.enum';

@Component({
  selector: 'ba-legal-person-data-step',
  templateUrl: './legal-person-data-step.component.html',
  styleUrls: ['./legal-person-data-step.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class LegalPersonDataStepComponent extends Unsubscriber() implements OnInit, IStep<void> {
  @Input() ruc!: string;
  @Input() isClient: boolean = false;
  @Input() usePartnersForm: boolean = false;

  @Output() stepDone = new EventEmitter<void>();

  showContinueDisclaimer: boolean = false;
  showErrorDisclaimer: boolean = false;
  isForbiddenSubsector: boolean = false;
  isForbiddenSector: boolean = false;
  showOutsideTax: boolean = false;
  showStopFlow = false;
  ibkConfig = setMaxDatePicker(1);

  company$!: ReturnType<CustomersFacade['fetchCompany']>;
  hasPartnersForm: boolean = false;
  isLoading = false;
  showToastBusinessActivity: boolean = false;
  disableBtnContinue: boolean = false;

  rubros: Rubro[] = [
    { value: '01', label: 'Alquileres' },
    { value: '02', label: 'Mineria' },
    { value: '03', label: 'Servicios comerciales' },
    { value: '04', label: 'Servicios Gubernamentales, Públicos ó Partidos Políticos', isForbidden: true },
    { value: '05', label: 'Servicios profesionales' },
    { value: '06', label: 'Servicio de transporte, carga y/o logística' },
    { value: '07', label: 'Servicios y/o ventas minoristas' },
    { value: '08', label: 'Servicios agrícolas' },
    { value: '09', label: 'Servicios varios' },
    { value: '10', label: 'Construcción e Inmobiliarias' },
    { value: '11', label: 'Otros Rubros' },
  ];

  subrubros: any[] = [
    { value: '0101', label: 'Alquiler de autos' },
    { value: '0102', label: 'Alquiler Comercial' },
    { value: '0103', label: 'Alquiler inmuebles' },

    { value: '0201', label: 'Minería de oro', isForbidden: true },
    { value: '0202', label: 'Servicios relacionados a la minería' },
    { value: '0203', label: 'Otras mineras' },

    { value: '0301', label: 'Consultoría y/o Asesorías generales' },
    { value: '0302', label: 'Joyería' },
    { value: '0303', label: 'Bazar y/o juguetería' },
    { value: '0304', label: 'Mantenimiento y reparaciones' },
    { value: '0305', label: 'Entretenimiento' },
    { value: '0306', label: 'Otros' },

    { value: '0501', label: 'Asociaciones, comités ó fundaciones' },
    { value: '0502', label: 'Instituciones educativas' },
    { value: '0503', label: 'Consorcios o sindicatos', isForbidden: true },
    { value: '0504', label: 'Colegios profesionales', isForbidden: true },
    { value: '0505', label: 'Organizaciones, instituciones ó federaciones' },
    { value: '0506', label: 'Salud y medicina' },
    { value: '0507', label: 'Otros' },

    { value: '0701', label: 'Distribución, suministros ó mercancías' },
    { value: '0702', label: 'Automotriz' },
    { value: '0703', label: 'Ferreterías' },
    { value: '0704', label: 'Tiendas y/o bodegas' },
    { value: '0705', label: 'Otros' },

    { value: '0901', label: 'Clubs, gimnasios y otras actividades deportivas' },
    { value: '0902', label: 'Comunidades o cooperativas', isForbidden: true },
    { value: '0903', label: 'Financieras o fintech', isForbidden: true },
    { value: '0904', label: 'Junta de propietarios' },
    { value: '0905', label: 'Hoteles, viajes y/o turismo' },
    { value: '0906', label: 'Proveedores, mercancía u otros' },
    { value: '0907', label: 'Restaurantes, pastelerías y/o bares' },
    { value: '0908', label: 'Seguros de salud y similares' },
    { value: '0909', label: 'Otros' },
  ];

  subrubrosList: never[] | any[] = [];
  addressForm = (this.controlContainer.control as FormGroup).get('address') as FormGroup;

  constructor(
    private readonly controlContainer: ControlContainer,
    private readonly customersFacade: CustomersFacade,
    public readonly paramsService: ParametersService,
    private readonly sessionService: SessionService,
    private readonly traceService: TraceService
  ) {
    super();
    const { personType } = this.sessionService.getCommonInfoValue;
    if (personType == undefined) this.showOutsideTax = true;
  }

  ngOnInit(): void {
    this.ibkConfig = setMaxDatePicker();
    this.form.get('companyData')?.get('ruc')?.disable();
    this.form.get('companyData')?.get('ruc')?.setValue(this.ruc);
    this.company$ = this.customersFacade.fetchCompany(this.ruc).pipe(
      tap((res) => {
        if (!res.company.societyType) {
          this.showStopFlow = true;
          this.form.disable();
          this.form.get('companyData')?.disable();
          if (this.showOutsideTax)
            this.form.get('foreignTaxDetails')?.disable();
          this.form.get('areSectorAndSubsectorValid')?.disable();
          this.form.get('registrationNumber')?.disable();
          return;
        }
        if (res.company?.constitutionDate) {
          (this.form.get('companyData') as FormGroup)
            ?.get('constitutionDate')
            ?.setValue(res.company?.constitutionDate as any);
        }
        if (res.company?.societyType) {
          (this.form.get('companyData') as FormGroup)?.get('societyType')?.setValue(res.company?.societyType as any);
        }
        if (res?.company?.societyType === 'SA' || res.company?.societyType === 'SAC') {
          this.hasPartnersForm = true;
        }
      }),
      catchError((err) => {
        throw err;
      })
    );
    if (this.showOutsideTax)
      this.listenToForeignTaxForm();
    this.initializePartnersForm();
    this.listenToContributorForm();
    this.listenToSector();
    this.listenToSubsector();
  }

  get form() {
    return (this.controlContainer as FormGroupDirective).form;
  }

  get sectorDataForm() {
    return this.isClient
      ? getSectorDataClientFormInstance({ from: this.form })
      : getSectorDataNonClientFormInstance({ from: this.form });
  }

  initializePartnersForm() {
    if (!this.usePartnersForm) return;
    appendPartnerListForm({
      to: this.form,
      with: createPartnersListForm([this.createPartnerForm()]),
    });
    const { lastStep } = this.sessionService.getCommonInfoValue;
    if (lastStep == undefined || lastStep == false)
      this.addNewPartner();
  }

  addNewPartner() {
    getPartnerListFormInstance({ from: this.form }).push(this.createPartnerForm());
  }

  removePartner(i: number) {
    const form = getPartnerListFormInstance({ from: this.form });
    form.removeAt(i);
  }

  createPartnerForm() {
    const partnerForm = createPartnerForm();
    this.subsink.sink = onPartnerFormUserInput({
      on: partnerForm,
      when: (val) => val === 'CE',
      exec: (form) =>
        appendPartnerCEDataForm({
          to: form,
          with: createPartnerCEDataForm(),
        }),
      undo: (form) => removePartnerCEDataForm({ from: form }),
    });
    return partnerForm;
  }

  listenToForeignTaxForm() {
    this.subsink.sink = onOutsideTaxUserInput({
      on: this.form,
      affirmative: () => {
        this.showContinueDisclaimer = true;
        this.showErrorDisclaimer = false;
      },
      negative: () => {
        this.showContinueDisclaimer = false;
        this.showErrorDisclaimer = true;
      },
    });
  }

  listenToContributorForm() {
    const control = this.form.get('companyData')?.get('societyType')!;
    this.subsink.sink = control?.valueChanges.pipe(startWith(control.value)).subscribe((val) => {
      if (val === 'SA' || val === 'SAC') {
        this.usePartnersForm = true;
        this.initializePartnersForm();
      } else {
        this.usePartnersForm = false;
        removePartnerListForm({ from: this.form });
      }
    });
  }

  listenToSector() {
    this.setSectorCode(this.sectorDataForm.get('sectorCode')?.value);

    // this.form
    //   .get('sectorData')
    //   ?.get('sectorCode')
    //   ?.valueChanges.subscribe((sectorCode) => this.setSectorCode(sectorCode));

    this.form
      .get('sectorData')
      ?.get('sectorCode')
      ?.valueChanges.subscribe((sectorCode) => {
        this.isForbiddenSubsector = false;
        this.setSectorCode(sectorCode);
        const sector = this.rubros.find((sc) => sc.value === sectorCode);
        if (sector?.isForbidden) {
          this.isForbiddenSector = true;
          this.disableBtnContinue = true;
          this.form.disable({ emitEvent: false });
          this.form.get('sectorData')?.get('sectorCode')?.enable({ emitEvent: false });
          this.form.get('sectorData')?.get('subSectorCode')?.enable({ emitEvent: false });
        } else {
          this.isForbiddenSector = false;
          this.disableBtnContinue = false;
          this.form.enable({ emitEvent: false });
          this.form.get('companyData')?.get('ruc')?.disable();
        }
      });
  }

  setSectorCode(sectorCode: string) {
    if (!sectorCode) return;

    const subrubros = this.subrubros.filter((sb) => sb.value.slice(0, 2) === sectorCode);
    this.subrubrosList = subrubros;
    if (subrubros.length === 0) {
      this.sectorDataForm?.get('subSectorCode')?.removeValidators(Validators.required);
      this.sectorDataForm?.get('subSectorCode')?.disable();
      return;
    }
    // this.sectorDataForm?.get('subSectorCode')?.setValue(null);
    this.sectorDataForm?.get('subSectorCode')?.addValidators(Validators.required);
  }

  listenToSubsector() {
    this.form
      .get('sectorData')
      ?.get('subSectorCode')
      ?.valueChanges.subscribe((subSectorCode) => {
        this.isForbiddenSector = false;
        const subSector = this.subrubrosList.find((sc) => sc.value === subSectorCode);
        if (subSector?.isForbidden) {
          this.isForbiddenSubsector = true;
          this.disableBtnContinue = true;
          this.form.disable({ emitEvent: false });
          this.form.get('sectorData')?.get('sectorCode')?.enable({ emitEvent: false });
          this.form.get('sectorData')?.get('subSectorCode')?.enable({ emitEvent: false });
        } else {
          this.isForbiddenSubsector = false;
          this.disableBtnContinue = false;
          this.form.enable({ emitEvent: false });
          this.form.get('companyData')?.get('ruc')?.disable();
        }
      });
  }
  done() {
    this.sessionService.setCommonInfo = { lastStep: false };
    if (!this.isClient)
      this.sessionService.setCommonInfo = { email: this.form.get('companyData')?.get('email')?.value };
    this.isLoading = true;
    this.stepDone.emit();
    this.traceService.registerEvent(new VisitEvent(TraceEventName.ACTION_PERSONAL_DATA));
  }

  chBusinessActivity(event: any) {
    this.showToastBusinessActivity = this.form.get('areSectorAndSubsectorValid')?.value;
  }
}
